import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Public `/join/:slug` landing — the entrypoint for member self-service signup.
 *
 * Flow:
 *   - Anonymous visitor sees a tasteful club-themed landing with the club's
 *     public_about and logo (when set), and two paths:
 *       1. "Create your account" — fills the form below → POST /public/clubs/<slug>/join
 *       2. "Sign in" — link to /login?next=/join/<slug>; after login the
 *          frontend (this same page) detects the auth state and shows a
 *          "Request Access" button instead.
 *   - Logged-in member sees:
 *       - "You're already a member" with link to dashboard, OR
 *       - "Request Access" → POST /clubs/<slug>/join
 *
 * Backend gates everything via `auto_approve_members`. The page surfaces this
 * to set expectations ("Approved instantly" vs "Pending club approval").
 */
export default function PublicJoinPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { user, setUser } = useAuth();

  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Anonymous-signup form state. Pre-fill the email when the URL carries
  // ?email=<x> (set by the pro-shop "Invite member" flow) so the recipient
  // just confirms it and sets a password.
  const invitedEmail = (search.get("email") || "").trim().toLowerCase();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: invitedEmail, mobile_number: "", password: "",
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signupResult, setSignupResult] = useState(null);

  // Logged-in "Request Access" state
  const [joinResult, setJoinResult] = useState(null);

  // iter72o · guards the analytics ping against React StrictMode's dev-only
  // double-invoke — server-side dedupe would absorb it either way, but we
  // shouldn't emit two network requests for what is logically one visit.
  const pingedRef = useRef(false);

  useEffect(() => {
    // Use a fresh axios call (no withCredentials needed) so we don't 401
    // when an expired cookie sits in the browser.
    const baseURL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${baseURL}/api/public/clubs/${slug}`)
      .then((r) => setClub(r.data))
      .catch((err) => setError(err.response?.status === 404
        ? "We couldn't find that club. Check the link and try again."
        : (formatApiErrorDetail(err.response?.data?.detail) || err.message)))
      .finally(() => setLoading(false));

    // iter72o · Fire a single anonymous page-view for the Super Admin
    // "Join Page Analytics" dashboard. Session-deduped server-side so
    // refreshes within the hour don't inflate the count.
    if (pingedRef.current) return;
    pingedRef.current = true;
    try {
      let sid = window.sessionStorage.getItem("mcc.jpv.sid");
      if (!sid) {
        sid = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID().replace(/-/g, "")
          : `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
        window.sessionStorage.setItem("mcc.jpv.sid", sid);
      }
      axios.post(`${baseURL}/api/public/analytics/join-page-view`, {
        slug,
        session_id: sid,
        referrer: document.referrer || null,
        path: window.location.pathname,
      }).catch(() => { /* fire-and-forget — analytics never breaks the page */ });
    } catch { /* fire-and-forget */ }
  }, [slug]);

  const submitSignup = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.post(
        `${baseURL}/api/public/clubs/${slug}/join`,
        form,
        { withCredentials: true },
      );
      setSignupResult(data);
      if (data.membership_status === "active" && data.user) {
        setUser(data.user);
        setTimeout(() => navigate("/dashboard", { replace: true }), 800);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const requestAccess = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/clubs/${slug}/join`);
      setJoinResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="bg-cream min-h-screen flex items-center justify-center text-[#7A7A7A]">Loading…</div>;
  }
  if (error && !club) {
    return (
      <div className="bg-cream min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="join-not-found">
          <div className="overline">404</div>
          <h1 className="font-serif text-3xl mt-2 text-[#1A1A1A]">{error}</h1>
          <Link to="/login" className="mt-6 inline-flex btn-primary">Back to Sign In</Link>
        </div>
      </div>
    );
  }

  const autoApprove = !!club?.auto_approve_members;

  return (
    <div className="bg-[#0f1a14] min-h-screen text-[#F9F8F4]" data-testid="join-page">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <div className="overline" style={{ color: "#D9C68A" }}>You&rsquo;re Invited</div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight" data-testid="join-club-name">
          Join {club.name}
        </h1>
        {club.city && (
          <p className="text-sm uppercase tracking-[0.18em] text-[#9aa39a] mt-3">{club.city}</p>
        )}
        {club.public_about && (
          <p className="text-base text-[#D9D2B6] mt-6 leading-relaxed max-w-2xl">{club.public_about}</p>
        )}
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 text-[11px] uppercase tracking-[0.22em] border border-[#B59A5A]/40">
          {autoApprove
            ? <span className="text-[#B59A5A]">✓ Approved Instantly</span>
            : <span className="text-[#D9C68A]">Pending Club Approval</span>}
        </div>

        {error && (
          <div className="mt-6 px-4 py-3 text-sm border border-[#A33] bg-[#5a1a1a]/60 text-[#FFCFCF]" data-testid="join-error">
            {error}
          </div>
        )}

        {/* --- Already logged in --- */}
        {user && (
          <div className="mt-10">
            <LoggedInPanel
              user={user}
              club={club}
              joinResult={joinResult}
              busy={busy}
              onRequest={requestAccess}
            />
          </div>
        )}

        {/* --- Anonymous: signup form OR success card --- */}
        {!user && !signupResult && (
          <form onSubmit={submitSignup} className="mt-10 space-y-5" data-testid="join-signup-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <SignupField label="First name" value={form.first_name} testId="join-first-name"
                onChange={(v) => setForm({ ...form, first_name: v })} required />
              <SignupField label="Last name" value={form.last_name} testId="join-last-name"
                onChange={(v) => setForm({ ...form, last_name: v })} required />
            </div>
            <SignupField label="Email" type="email" value={form.email} testId="join-email"
              onChange={(v) => setForm({ ...form, email: v })} required />
            <SignupField label="Mobile number (optional)" type="tel" value={form.mobile_number} testId="join-mobile"
              onChange={(v) => setForm({ ...form, mobile_number: v })} />
            <SignupField label="Choose a password" type="password" value={form.password} testId="join-password"
              onChange={(v) => setForm({ ...form, password: v })} required hint="At least 8 characters." />

            {/* Privacy & Terms consent — required to submit. Phrased to
                reinforce the tenant-isolation commitment the user landed on
                this page expecting. */}
            <label className="flex items-start gap-3 text-[12px] text-[#9aa39a] leading-relaxed pt-1">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                data-testid="join-consent-checkbox"
                className="mt-1 accent-[#B59A5A] w-4 h-4"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener" className="underline text-[#D9C68A] hover:text-[#F9F8F4]" data-testid="join-terms-link">
                  Terms of Service
                </Link>{" "}
                and acknowledge the{" "}
                <Link to="/privacy" target="_blank" rel="noopener" className="underline text-[#D9C68A] hover:text-[#F9F8F4]" data-testid="join-privacy-link">
                  Privacy Policy
                </Link>{" "}
                describing how my data is tenant-isolated and never shared outside my club.
              </span>
            </label>

            <div className="flex items-center justify-between gap-4 pt-2 flex-wrap">
              <Link
                to={`/login?next=${encodeURIComponent(`/join/${slug}`)}`}
                data-testid="join-signin-link"
                className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A] underline-offset-4 hover:underline"
              >
                Already a My Crest Club member? Sign in →
              </Link>
              <button
                type="submit"
                disabled={busy || !agreedToTerms}
                data-testid="join-submit"
                className="px-5 py-2.5 text-[12px] uppercase tracking-[0.22em] bg-[#B59A5A] text-[#0f1a14] font-semibold hover:bg-[#A6894B] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy
                  ? "Submitting…"
                  : autoApprove ? "Create Account & Join" : "Request to Join"}
              </button>
            </div>
          </form>
        )}

        {!user && signupResult && (
          <SignupSuccess result={signupResult} club={club} />
        )}

        {search.get("next") && (
          <p className="mt-8 text-[11px] uppercase tracking-[0.22em] text-[#9aa39a]">
            After signing in we&rsquo;ll bring you back here.
          </p>
        )}
      </div>
    </div>
  );
}

function LoggedInPanel({ user, club, joinResult, busy, onRequest }) {
  // Is the user already a member?
  const existing = (user.clubs || []).find((c) => c.club_id === club.club_id);
  if (existing) {
    return (
      <div className="border border-[#B59A5A]/40 bg-[#1A422D]/40 p-6" data-testid="join-already-member">
        <div className="overline" style={{ color: "#D9C68A" }}>You&rsquo;re already on the roster</div>
        <p className="font-serif text-2xl mt-2">Welcome back, {user.name?.split(" ")[0]}.</p>
        <p className="text-sm text-[#D9D2B6] mt-2">
          Your membership at {club.name} is <strong>{existing.status || "active"}</strong>.
        </p>
        <Link to="/dashboard" className="mt-5 inline-flex btn-primary">Go to Dashboard →</Link>
      </div>
    );
  }
  if (joinResult) {
    return (
      <div className="border border-[#B59A5A]/40 bg-[#1A422D]/40 p-6" data-testid="join-request-result">
        <div className="overline" style={{ color: "#D9C68A" }}>Request received</div>
        <p className="font-serif text-2xl mt-2">
          {joinResult.membership_status === "active"
            ? "You're in."
            : "Sit tight — pending approval."}
        </p>
        <p className="text-sm text-[#D9D2B6] mt-2">
          {joinResult.membership_status === "active"
            ? `You can now switch into ${club.name} from your "My Clubs" dashboard.`
            : `The pro shop at ${club.name} has been notified. You'll get an email when they approve you.`}
        </p>
        <Link to="/dashboard" className="mt-5 inline-flex btn-primary">Back to Dashboard →</Link>
      </div>
    );
  }
  return (
    <div className="border border-[#B59A5A]/40 bg-[#1A422D]/40 p-6">
      <div className="overline" style={{ color: "#D9C68A" }}>You&rsquo;re signed in</div>
      <p className="font-serif text-2xl mt-2">Hello, {user.name?.split(" ")[0]}.</p>
      <p className="text-sm text-[#D9D2B6] mt-2 max-w-xl">
        We can add {club.name} to your membership. {club.auto_approve_members
          ? "You'll be active immediately."
          : "The club admin will review and approve."}
      </p>
      <button
        type="button"
        onClick={onRequest}
        disabled={busy}
        data-testid="join-request-access"
        className="mt-5 px-5 py-2.5 text-[12px] uppercase tracking-[0.22em] bg-[#B59A5A] text-[#0f1a14] font-semibold hover:bg-[#A6894B] disabled:opacity-60"
      >
        {busy ? "Submitting…" : "Request Access"}
      </button>
    </div>
  );
}

function SignupSuccess({ result, club }) {
  const isActive = result.membership_status === "active";
  return (
    <div className="mt-10 border border-[#B59A5A]/40 bg-[#1A422D]/40 p-6" data-testid="join-signup-result">
      <div className="overline" style={{ color: "#D9C68A" }}>
        {isActive ? "Welcome aboard" : "Request received"}
      </div>
      <p className="font-serif text-2xl mt-2">
        {isActive ? `You're in.` : `Sit tight — pending approval.`}
      </p>
      <p className="text-sm text-[#D9D2B6] mt-2 max-w-xl">
        {isActive
          ? `Taking you to your dashboard…`
          : `${club.name}'s pro shop has been notified of your request. We'll email you the moment they approve.`}
      </p>
    </div>
  );
}

function SignupField({ label, value, onChange, type = "text", required, hint, testId }) {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && showPw ? "text" : type;
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={effectiveType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          data-testid={testId}
          className={`w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] h-11 px-3 focus:outline-none focus:border-[#B59A5A] ${isPassword ? "pr-10" : ""}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            tabIndex={-1}
            aria-label={showPw ? "Hide password" : "Show password"}
            data-testid={testId ? `${testId}-eye` : undefined}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#9aa39a] hover:text-[#D9C68A] focus:text-[#D9C68A] focus:outline-none"
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
      {hint && <p className="text-[10px] text-[#9aa39a] mt-1">{hint}</p>}
    </div>
  );
}
